import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthHeader from './components/AuthHeader';
import AuthTabs from './components/AuthTabs';
import SocialAuthButton from './components/SocialAuthButton';
import AuthDivider from './components/AuthDivider';
import LoginForm from './components/LoginForm';
import SignUpForm from './components/SignUpForm';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const Authentication = () => {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const { t, language } = useLanguage();

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setAuthError('');
    setSignUpSuccess(false);
    const { data, error } = await signIn(formData?.email, formData?.password);
    setIsLoading(false);
    if (error) {
      setAuthError(error?.message || 'Invalid email or password. Please check your credentials and try again.');
      return;
    }
    // Redirect based on role
    const role = data?.user?.user_metadata?.role || 'buyer';
    if (role === 'admin') {
      navigate('/admin-dashboard');
    } else if (role === 'agent') {
      navigate('/agent-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handleSignUp = async (formData) => {
    setIsLoading(true);
    setAuthError('');
    setSignUpSuccess(false);
    const { data, error } = await signUp(formData?.email, formData?.password, {
      full_name: formData?.fullName,
      role: 'buyer'
    });
    setIsLoading(false);
    if (error) {
      setAuthError(error?.message || 'Failed to create account. Please try again.');
      return;
    }
    if (data?.session) {
      // Logged in immediately (email confirmation disabled)
      navigate('/dashboard');
    } else if (data?.user) {
      // Email confirmation may be required — switch to login tab with success message
      setActiveTab('login');
      setSignUpSuccess(true);
    } else {
      setAuthError('Account created but could not sign in automatically. Please log in.');
      setActiveTab('login');
    }
  };

  const handleSocialAuth = async (provider) => {
    setIsLoading(true);
    setAuthError('');
    try {
      const { error } = await import('../../lib/supabase')?.then(m =>
        m?.supabase?.auth?.signInWithOAuth({
          provider,
          options: { redirectTo: `${window.location?.origin}/dashboard` }
        })
      );
      if (error) setAuthError(error?.message);
    } catch (err) {
      setAuthError('Social login failed. Please try again.');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8 md:py-12">
      <div className="w-full max-w-md">
        <AuthHeader activeTab={activeTab} />

        <div className="bg-card rounded-xl shadow-elevation-lg border border-border p-6 md:p-8">
          <AuthTabs activeTab={activeTab} onTabChange={(tab) => { setActiveTab(tab); setAuthError(''); setSignUpSuccess(false); }} />

          <SocialAuthButton
            provider="google"
            onClick={() => handleSocialAuth('google')}
            disabled={isLoading}
          />

          <AuthDivider />

          {signUpSuccess && (
            <div className="mb-4 md:mb-6 p-3 md:p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-success font-medium font-body">Account created successfully!</p>
                <p className="text-xs text-success/80 mt-0.5">Please check your email to confirm your account, then log in below.</p>
              </div>
            </div>
          )}

          {authError && (
            <div className="mb-4 md:mb-6 p-3 md:p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error font-body">{authError}</p>
            </div>
          )}

          {activeTab === 'login' ? (
            <LoginForm onSubmit={handleLogin} isLoading={isLoading} />
          ) : (
            <SignUpForm onSubmit={handleSignUp} isLoading={isLoading} />
          )}

          <div className="mt-6 pt-6 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              {activeTab === 'login' ? (language === 'bn' ? 'অ্যাকাউন্ট নেই? ' : "Don't have an account? ") : (language === 'bn' ? 'ইতিমধ্যে অ্যাকাউন্ট আছে? ' : 'Already have an account? ')}
              <button
                onClick={() => {
                  setActiveTab(activeTab === 'login' ? 'signup' : 'login');
                  setAuthError('');
                  setSignUpSuccess(false);
                }}
                className="text-primary hover:text-primary/80 font-medium transition-colors"
                disabled={isLoading}
              >
                {activeTab === 'login' ? (language === 'bn' ? 'নিবন্ধন করুন' : 'Sign up') : (language === 'bn' ? 'লগইন করুন' : 'Login')}
              </button>
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              {language === 'bn' ? 'আপনি কি একজন পেশাদার? ' : 'Are you a professional? '}
              <button
                onClick={() => navigate('/join-as-pro')}
                className="text-primary hover:text-primary/80 font-medium transition-colors"
                disabled={isLoading}
              >
                {language === 'bn' ? 'পেশাদার হিসেবে যোগ দিন' : 'Join as a Pro'}
              </button>
            </p>
          </div>
        </div>

        <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
          <p className="text-xs text-muted-foreground text-center font-medium mb-2">Demo Credentials</p>
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>🛒 Buyer: buyer@listinglens.com / password123</p>
            <p>🏠 Agent: agent@listinglens.com / agent123</p>
            <p>⚙️ Admin: admin@listinglens.com / admin123</p>
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs md:text-sm text-muted-foreground">
            {language === 'bn' ? 'চালিয়ে যাওয়ার মাধ্যমে আপনি ListingLens-এর ' : 'By continuing, you agree to ListingLens '}
            <a href="/terms-of-service" className="text-primary hover:text-primary/80">{t('termsOfService')}</a>
            {language === 'bn' ? ' এবং ' : ' and '}
            <a href="/privacy-policy" className="text-primary hover:text-primary/80">{t('privacyPolicy')}</a>
            {language === 'bn' ? '-এ সম্মত হচ্ছেন' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Authentication;